import React, { useState } from "react";
import toast from "react-hot-toast";

import axiosInstant from "../../../../api";
import { publicCreateBrief, publicUpdateBrief } from "../../../../api/endpoints";
import { BriefForm, BriefFormHelpers } from "../../../../components/Brief/BriefForm";
import { IExpertQuestions, IVideoContent } from "../../../../components/Brief/constants";
import { PublicProject } from "../OrderPublicPage";

interface Props {
    project: PublicProject;
    token: string;
    onRefresh: () => void;
    publicBase?: string;
}

export function PublicBriefSection({ project, token, onRefresh, publicBase = "brand-orders" }: Props) {
    const [submitting, setSubmitting] = useState(false);
    const [videoContent, setVideoContent] = useState<Partial<IVideoContent>>({});

    // Adapt PublicProject to the shape BriefForm expects (reads project.briefs[0])
    const adaptedProject: any = {
        id: project.id,
        briefs: project.brief
            ? [
                  {
                      id: project.brief.id,
                      attachments: project.brief.attachments,
                      ...(project.brief.intakeForm || {}),
                      ...(project.brief.questions || {})
                  }
              ]
            : undefined
    };

    const buildPayload = (expertData: IExpertQuestions, isSubmit: boolean) => {
        const combined = { ...videoContent, ...expertData };
        delete (combined as any).status;
        const [expert_age_min, expert_age_max] = expertData.ageRange || [];
        return { ...combined, expert_age_min, expert_age_max, ageRange: undefined, isSubmit };
    };

    const persist = async (data: any, helpers: BriefFormHelpers) => {
        const response = project.brief?.id
            ? await axiosInstant.patch(publicUpdateBrief(token, project.id, project.brief.id, publicBase), data)
            : await axiosInstant.post(publicCreateBrief(token, project.id, publicBase), data);
        const briefId = response.data?.id || project.brief?.id;
        if (briefId) await helpers.uploadAttachments(briefId);
    };

    const handleSubmit = async (expertData: IExpertQuestions, helpers: BriefFormHelpers) => {
        setSubmitting(true);
        try {
            await persist(buildPayload(expertData, true), helpers);
            toast.success(project.brief?.id ? "Brief updated." : "Brief submitted.");
            onRefresh();
        } catch (err: any) {
            toast.error(err?.message || "Failed to submit brief.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSave = async (vals: any, helpers: BriefFormHelpers) => {
        setSubmitting(true);
        try {
            const combined = { ...videoContent, ...vals } as any;
            delete combined.status;
            const [expert_age_min, expert_age_max] = vals.ageRange || [];
            const data = { ...combined, expert_age_min, expert_age_max, ageRange: undefined };
            await persist(data, helpers);
            toast.success("Brief saved.");
            onRefresh();
        } catch (err: any) {
            toast.error(err?.message || "Failed to save brief.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <BriefForm
            project={adaptedProject}
            publicToken={token}
            publicBase={publicBase}
            loading={submitting}
            onCancel={() => {}}
            onChange={(data) => setVideoContent((prev) => ({ ...prev, ...data }))}
            onSubmit={handleSubmit}
            onSave={handleSave}
        />
    );
}
